import { FunctionComponent, useEffect, useState } from "react"
import { Button, Col, Form, Modal } from "react-bootstrap"
import { Door, PassDoor } from "../data/models.interface"
import ItemSelect from "./ItemSelect"
import { formatDate } from "../utils/AccessControlFormatter"

interface IPassDoorModal {
	onHide: () => void
	passDoors: PassDoor[]
	setPassDoors: (p: PassDoor[]) => any
	index: number | null
	doors: Door[]
}
const PassDoorModal: FunctionComponent<IPassDoorModal> = ({ onHide, passDoors, setPassDoors, index, doors }) => {
	// TODO handle validation, stop saving and show errors
	const [door, setDoor] = useState<Door>()
	const [startDate, setStartDate] = useState("")
	const [endDate, setEndDate] = useState("")
	const [dailyStartTime, setDailyStartTime] = useState("")
	const [dailyEndTime, setDailyEndTime] = useState("")
	const [touched, setTouched] = useState({ doorId: false, dailyStartTime: false, dailyEndTime: false })
	useEffect(() => {
		if (index != null) {
			setDoor(doors.find((d) => d.id === passDoors?.[index]?.doorId))
			setStartDate(formatDate(passDoors?.[index]?.startDate))
			setEndDate(formatDate(passDoors?.[index]?.endDate))
			setDailyStartTime(passDoors?.[index]?.dailyStartTime || "00:00")
			setDailyEndTime(passDoors?.[index]?.dailyEndTime || "24:00")
			setTouched({ doorId: false, dailyStartTime: false, dailyEndTime: false })
		}
	}, [passDoors, index, doors])
	//
	const errors = validate(door, dailyStartTime, dailyEndTime)
	//
	const save = () => {
		if (index != null) {
			// the modal shouldn't be open if index is undefined
			// show errors on all fields
			setTouched({ doorId: true, dailyStartTime: true, dailyEndTime: true })
			// don't save if there are errors
			if (errors.doorError || errors.dailyStartTimeError || errors.dailyEndTimeError) {
				return
			}
			//
			if (passDoors == null) {
				passDoors = [] // in case this is the first row
			} else {
				passDoors = [...passDoors] // make sure useState registers it as a new object
			}
			// add a new row if this is an add
			if (passDoors.length <= index) passDoors.push({} as PassDoor)
			// then we can edit the new or existing row the same way
			passDoors[index].doorId = door?.id!
			passDoors[index].doorName = door?.name!
			passDoors[index].doorFullName = door?.fullName!
			passDoors[index].startDate = startDate || "2000-01-01"
			passDoors[index].endDate = endDate || "2999-12-31"
			passDoors[index].dailyStartTime = dailyStartTime
			passDoors[index].dailyEndTime = dailyEndTime
			setPassDoors(passDoors) // update state
			onHide() // close the window
		}
	}
	const remove = () => {
		// the modal shouldn't be open if index is undefined
		// the remove button shouldn't be visible if passDoors is undefined
		if (passDoors && index != null) {
			passDoors = [...passDoors]
			passDoors.splice(index, 1)
			setPassDoors(passDoors) // update state
			onHide() // close the window
		}
	}
	//
	return (
		<Modal show={index != null} onHide={onHide}>
			<Modal.Header closeButton={true}>
				<Modal.Title>Edit Door</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form.Group className="mb-3" controlId="door">
					<Form.Label>Door</Form.Label>
					<ItemSelect
						placeholder="--Select a Door--"
						required
						valueId={door?.id}
						onChange={setDoor}
						onBlur={() => setTouched({ ...touched, doorId: true })}
						items={doors}
						convert={(d) => ({ id: d.id, name: d.fullName || d.name })}
						isInvalid={touched.doorId && errors.doorError != null}
					/>
					<Form.Control.Feedback type="invalid">{errors.doorError}</Form.Control.Feedback>
				</Form.Group>
				<Form.Group className="mb-3" controlId="startDate">
					<Form.Label>Start Date</Form.Label>
					<Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
				</Form.Group>
				<Form.Group className="mb-3" controlId="endDate">
					<Form.Label>End Date</Form.Label>
					<Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
				</Form.Group>
				<Form.Group className="mb-3" controlId="dailyStartTime">
					<Form.Label>Start Time</Form.Label>
					<Form.Control
						value={dailyStartTime}
						onChange={(e) => setDailyStartTime(e.target.value)}
						onBlur={() => setTouched({ ...touched, dailyStartTime: true })}
						isInvalid={touched.dailyStartTime && errors.dailyStartTimeError != null}
					/>
					<Form.Control.Feedback type="invalid">{errors.dailyStartTimeError}</Form.Control.Feedback>
				</Form.Group>
				<Form.Group className="mb-3" controlId="dailyEndTime">
					<Form.Label>End Time</Form.Label>
					<Form.Control
						value={dailyEndTime}
						onChange={(e) => setDailyEndTime(e.target.value)}
						onBlur={() => setTouched({ ...touched, dailyEndTime: true })}
						isInvalid={touched.dailyEndTime && errors.dailyEndTimeError != null}
					/>
					<Form.Control.Feedback type="invalid">{errors.dailyEndTimeError}</Form.Control.Feedback>
				</Form.Group>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" disabled={false} onClick={onHide}>
					Cancel
				</Button>
				<Button variant="danger" hidden={index != null && index >= (passDoors?.length || 0)} onClick={remove}>
					Remove
				</Button>
				<Col></Col>
				<Button variant="primary" disabled={false} onClick={save}>
					OK
				</Button>
			</Modal.Footer>
		</Modal>
	)
}
export default PassDoorModal

const validate = (door?: Door, dailyStartTime?: string, dailyEndTime?: string) => {
	const doorError = !door ? "Door is required." : null
	const dailyStartTimeError = !dailyStartTime ? "Start Time is required." : null
	const dailyEndTimeError = !dailyEndTime ? "End Time is required." : null
	return { doorError, dailyStartTimeError, dailyEndTimeError }
}
